import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import { useGetRole } from 'hooks/ui/useGetRole';
import '@silevis/reactgrid/styles.css';
import ReactGridFormV2 from './ReactGridFormV2';
import PreviewTableV2 from './PreviewTableV2';
import { INVESTOR_UPLOAD } from 'adminConstants/routes';
import { useFunds } from 'hooks/data/fund/useFunds';
import { useAdvisersLeanResponse } from 'hooks/data/adviser/useAdvisers';
import { styled } from '@mui/material/styles';
import { useListFirmBankDetails } from 'hooks/data/firm/useFirmBankDetails';
import { firmBankDetailsDisplay } from 'further-ui/utils';
import { FirmSelector } from 'components/FirmSelector';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import PageContent from 'components/PageContent';
import Heading from 'components/Heading';
import api from 'lib/trpcClient';
import UploadStatusV2 from './UploadStatusV2';
import type { UploadPreviewState, UploadSaveResultData } from './types';
import { InvestmentPaymentType } from 'further-types/payment';
import {
  createInitialGridData,
  GRID_DATA_STORAGE_KEY,
  GridData,
} from './helpers/gridHelpersV2';
import { useLocalStorage } from 'usehooks-ts';

const STEPS = {
  CREATE_GRID_DATA: 0,
  PREVIEW_UPLOAD: 1,
  UPLOAD_STATUS: 2,
};

const UPLOAD_STATE_STORAGE_KEY = 'investorUploadV2State';

const UPLOAD_ID_PARAM = 'uploadId';
const FIRM_ID_PARAM = 'firmId';

const isValidMongoId = (value: string | null): value is string =>
  typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

const getStatusParamsFromSearch = (
  search: string,
): { uploadId: string; firmId: string } | null => {
  const params = new URLSearchParams(search);
  const uploadId = params.get(UPLOAD_ID_PARAM);
  const firmId = params.get(FIRM_ID_PARAM);

  if (isValidMongoId(uploadId) && isValidMongoId(firmId)) {
    return { uploadId, firmId };
  }
  return null;
};

const buildStatusUrl = (uploadId: string, firmId: string) =>
  `${INVESTOR_UPLOAD}?${UPLOAD_ID_PARAM}=${encodeURIComponent(
    uploadId,
  )}&${FIRM_ID_PARAM}=${encodeURIComponent(firmId)}`;

type StoredUploadState = {
  activeStep: number;
  uploadId: string | null;
  firmId: string | null;
};

const loadStoredUploadState = (): StoredUploadState | null => {
  const stored = sessionStorage.getItem(UPLOAD_STATE_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (
      typeof parsed?.activeStep !== 'number' ||
      (parsed.uploadId !== null && typeof parsed.uploadId !== 'string') ||
      (parsed.firmId !== null && typeof parsed.firmId !== 'string')
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const saveStoredUploadState = (state: StoredUploadState) => {
  sessionStorage.setItem(UPLOAD_STATE_STORAGE_KEY, JSON.stringify(state));
};

const clearStoredUploadState = () => {
  sessionStorage.removeItem(UPLOAD_STATE_STORAGE_KEY);
};

const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PageContainer heading="Investors: Upload investors and subscriptions (V2)">
    {children}
  </PageContainer>
);

const FirmSelectorWrapper = styled('div')({
  marginBottom: 24,
  maxWidth: '20rem',
});

const Paragraph = styled('div')({
  marginBottom: 24,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '82rem',
  gap: 8,
});

const UploadInvestorInvestmentV2: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [gridDropdownsData, setGridDropdownsData] = useState<
    Record<string, undefined | Array<{ label: string; value: string }>>
  >({
    tranche: [],
    adviser: [],
    additional_linked_adviser: [],
    new_investor_legal_entity_adviser: [],
    new_investor_adviser: [],
    paid_to: [],
  });
  const { firmId: usersFirmId, isSuperAdmin } = useGetRole();
  const [activeStep, setActiveStep] = useState(STEPS.CREATE_GRID_DATA);
  const [uploadPreviewState, setUploadPreviewState] =
    useState<UploadPreviewState>({
      totalInvestors: 0,
      existingInvestorAccounts: [],
    });
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [uploadStatusSeed, setUploadStatusSeed] =
    useState<UploadSaveResultData | null>(null);
  const [firmId, setFirmId] = useState(usersFirmId);
  const [, setGridData] = useLocalStorage<GridData>(
    GRID_DATA_STORAGE_KEY,
    createInitialGridData(),
  );
  const firmIdChangeDisclosure = useDisclosure();

  const { tranches } = useFunds({
    firmId,
    fullyDeployed: false,
    disabled: !firmId,
  });

  const { data: coreFeeTemplates } = api.coreFeeTemplates.list.useQuery({
    firmId,
    order: 'desc',
    orderBy: 'createdAt',
  });

  const { data: performanceFeeTemplates } =
    api.performanceFeeTemplates.list.useQuery({
      firmId,
      order: 'desc',
      orderBy: 'createdAt',
    });

  const { data } = useAdvisersLeanResponse({
    firmId,
    order: 'asc',
    orderBy: 'fullName',
  });

  const firmBankDetails = useListFirmBankDetails(firmId);

  useEffect(() => {
    const fromUrl = getStatusParamsFromSearch(location.search);

    if (fromUrl) {
      setUploadId(fromUrl.uploadId);
      setFirmId(fromUrl.firmId);
      setActiveStep(STEPS.UPLOAD_STATUS);
      return;
    }
    const stored = loadStoredUploadState();
    if (stored?.activeStep === STEPS.UPLOAD_STATUS && stored.uploadId) {
      setUploadId(stored.uploadId);
      if (stored.firmId) {
        setFirmId(stored.firmId);
      }
      setActiveStep(STEPS.UPLOAD_STATUS);
    }
  }, []);

  useEffect(() => {
    if (activeStep === STEPS.CREATE_GRID_DATA && !uploadId) {
      clearStoredUploadState();
      return;
    }
    saveStoredUploadState({ activeStep, uploadId, firmId: firmId ?? null });
  }, [activeStep, uploadId, firmId]);

  useEffect(() => {
    if (
      activeStep === STEPS.UPLOAD_STATUS &&
      uploadId &&
      firmId &&
      !getStatusParamsFromSearch(location.search)
    ) {
      history.replace(buildStatusUrl(uploadId, firmId));
    }
  }, [activeStep, uploadId, firmId, history, location.search]);

  useEffect(
    function setDropdownListsData() {
      if (tranches.data && data?.advisers) {
        const tranchesList = tranches.data.map(
          ({ _id, fundName, firmId: { firmName } }) => ({
            label: `${firmName} ${fundName}`,
            value: _id,
          }),
        );

        const advisersList = data.advisers.map(
          ({ _id, fullName, email, organisation }) => {
            const name = fullName ? `${fullName} (${email})` : email;
            const org = organisation?.name ? ` [${organisation.name}]` : '';
            return {
              label: `${name}${org}`,
              value: _id,
            };
          },
        );

        const bankDetailsList = firmBankDetails?.data?.map((bankDetails) => ({
          label: firmBankDetailsDisplay(bankDetails),
          value: bankDetails._id,
        }));

        const listSorter = ({ label: labelA }, { label: labelB }) =>
          labelA.localeCompare(labelB);

        tranchesList.sort(listSorter);
        advisersList.sort(listSorter);
        if (bankDetailsList?.length) bankDetailsList.sort(listSorter);

        setGridDropdownsData((gridDropdownsData) => ({
          ...gridDropdownsData,
          tranche: tranchesList,
          adviser: advisersList,
          additional_linked_adviser: advisersList,
          new_investor_legal_entity_adviser: advisersList,
          new_investor_adviser: advisersList,
          paid_to: [
            {
              label: 'Cash balance payment',
              value: InvestmentPaymentType.CashBalance,
            },
            ...(bankDetailsList ?? []),
          ],
          core_fee_template_id: coreFeeTemplates?.result.map(
            ({ _id, name }) => ({
              label: name,
              value: _id,
            }),
          ),
          performance_fee_template_id: performanceFeeTemplates?.result.map(
            ({ _id, name }) => ({
              label: name,
              value: _id,
            }),
          ),
        }));
      }
    },
    [
      tranches.data,
      data?.advisers,
      firmBankDetails?.data,
      coreFeeTemplates,
      performanceFeeTemplates,
    ],
  );

  const onFirmChange = (newFirmId: string) => {
    if (firmId && firmId !== newFirmId) {
      firmIdChangeDisclosure.stageAction({ newFirmId });
    } else {
      setFirmId(newFirmId);
    }
  };

  const onFirmChangeConfirm = () => {
    setFirmId(firmIdChangeDisclosure.actionProps.newFirmId);
    firmIdChangeDisclosure.onClose();
  };

  const uploadPreviewSet = (uploadPreviewState: UploadPreviewState) => {
    setUploadId(null);
    setUploadStatusSeed(null);
    setUploadPreviewState(uploadPreviewState);
    setActiveStep(STEPS.PREVIEW_UPLOAD);
  };

  const uploadStatusSet = (uploadResult: UploadSaveResultData) => {
    setUploadId(uploadResult.uploadId);
    setUploadStatusSeed(uploadResult);
    setActiveStep(STEPS.UPLOAD_STATUS);
  };

  const handleRestart = () => {
    const initialGridData = createInitialGridData();
    localStorage.setItem(
      GRID_DATA_STORAGE_KEY,
      JSON.stringify(initialGridData),
    );
    setGridData(initialGridData);
    clearStoredUploadState();
    setUploadId(null);
    setUploadStatusSeed(null);
    setActiveStep(STEPS.CREATE_GRID_DATA);
    history.replace(INVESTOR_UPLOAD);
  };

  if (activeStep === STEPS.CREATE_GRID_DATA)
    return (
      <Wrapper>
        <PageContent>
          {isSuperAdmin && (
            <FirmSelectorWrapper>
              <FirmSelector
                onChange={onFirmChange}
                firmId={firmId}
                placeholder="Select a firm"
              />
            </FirmSelectorWrapper>
          )}
          {firmId && (
            <>
              <Heading variant="h2">
                Input your investors and subscriptions into the table
              </Heading>
              <Paragraph>
                <p>
                  <b>How to use this feature:</b> The below upload table can be
                  used for uploading one or more investors and subscriptions at
                  the same time. Type or paste information into cells to
                  continue. Leaving this page without submitting can cause
                  entered data to be lost. If an email address is already in use
                  on the platform and the identity date matches that account,
                  any associated subscriptions will be added to the relevant
                  existing investor's account.
                </p>
              </Paragraph>
              <ReactGridFormV2
                gridDropdownsData={gridDropdownsData}
                uploadPreviewSet={uploadPreviewSet}
                firmId={firmId}
              />
            </>
          )}
        </PageContent>
        <AlertDialog
          open={firmIdChangeDisclosure.isOpen}
          onClose={firmIdChangeDisclosure.onClose}
          onConfirm={onFirmChangeConfirm}
          title="Change firm?"
          content="This will reset the values in the tranche, adviser and paid to dropdowns."
        />
      </Wrapper>
    );

  if (activeStep === STEPS.PREVIEW_UPLOAD)
    return (
      <Wrapper>
        <PageContent>
          <PreviewTableV2
            totalInvestors={uploadPreviewState.totalInvestors}
            existingInvestorAccounts={
              uploadPreviewState.existingInvestorAccounts
            }
            handleSubmitCallback={uploadStatusSet}
            handleBack={() => setActiveStep(STEPS.CREATE_GRID_DATA)}
            tranches={tranches?.data ?? []}
            firmId={firmId}
            gridDropdownsData={gridDropdownsData}
          />
        </PageContent>
      </Wrapper>
    );

  if (activeStep === STEPS.UPLOAD_STATUS && uploadId && firmId) {
    return (
      <Wrapper>
        <UploadStatusV2
          uploadId={uploadId}
          firmId={firmId}
          initialStatus={uploadStatusSeed ?? undefined}
          onRestart={handleRestart}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <PageContent>
        {activeStep === STEPS.UPLOAD_STATUS ? (
          <>
            <Heading variant="h2">Upload status</Heading>
            <p>Loading upload status...</p>
          </>
        ) : (
          <>
            <Heading variant="h2">Upload status</Heading>
            <p>
              We could not load the upload status. Please return to the upload
              screen to start a new upload.
            </p>
            <p>
              <a href={INVESTOR_UPLOAD}>Back to upload</a>
            </p>
          </>
        )}
      </PageContent>
    </Wrapper>
  );
};

export default UploadInvestorInvestmentV2;
